/** Data hooks for §7 Quotations & approvals. */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as quotationsApi from '../../api/quotations.api.js';
import * as approvalsApi from '../../api/approvals.api.js';
import * as accountsApi from '../../api/accounts.api.js';
import * as opportunitiesApi from '../../api/opportunities.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast } from '../ui/uiSlice.js';

export function useQuotations(params) {
  return useQuery({
    queryKey: queryKeys.quotations.list(params),
    queryFn: () => quotationsApi.listQuotations(params),
    placeholderData: (previous) => previous,
  });
}

export function useQuotation(id) {
  return useQuery({
    queryKey: queryKeys.quotations.detail(id),
    queryFn: () => quotationsApi.getQuotation(id),
    enabled: Boolean(id),
  });
}

/** Every version sharing a lineage, oldest first — either a root id or any later version's id works. */
export function useQuotationVersions(rootId, enabled = true) {
  return useQuery({
    queryKey: queryKeys.quotations.versions(rootId),
    queryFn: () => quotationsApi.listQuotations({ root_quotation_id: rootId, current_only: 'false', sort: 'created_at' }),
    enabled: Boolean(rootId) && enabled,
  });
}

function invalidateQuote(queryClient, id) {
  queryClient.invalidateQueries({ queryKey: queryKeys.quotations.all });
  if (id) queryClient.invalidateQueries({ queryKey: queryKeys.quotations.detail(id) });
}

export function useCreateQuotation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: (payload) => quotationsApi.createQuotation(payload),
    onSuccess: (q) => {
      invalidateQuote(queryClient);
      dispatch(pushToast({ tone: 'success', message: `Quotation ${q.quote_no} created.` }));
    },
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not create that quotation', detail: error.message })),
  });
}

export function useUpdateQuotation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ id, patch, rowVersion }) => quotationsApi.updateQuotation(id, patch, rowVersion),
    onSuccess: (q) => invalidateQuote(queryClient, q.id),
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      const locked = error?.code === 'quotation_locked';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : locked ? 'This quotation is locked' : 'Could not save that change',
        detail: stale ? 'Refresh to pick up their version, then try again.' : locked ? 'Revise it instead to change an approved or sent quotation.' : error.message,
      }));
    },
  });
}

export function useDeleteQuotation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ id, reason, rowVersion }) => quotationsApi.deleteQuotation(id, { reason, rowVersion }),
    onSuccess: () => {
      invalidateQuote(queryClient);
      dispatch(pushToast({ tone: 'success', message: 'Quotation deleted.' }));
    },
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not delete that quotation', detail: error.message })),
  });
}

export function useReviseQuotation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ id, payload }) => quotationsApi.reviseQuotation(id, payload),
    onSuccess: (q) => {
      invalidateQuote(queryClient);
      dispatch(pushToast({ tone: 'success', message: `Revision ${q.version_no} created.` }));
    },
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not create a revision', detail: error.message })),
  });
}

export function useSubmitForApproval() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ id, payload }) => quotationsApi.submitForApproval(id, payload),
    onSuccess: (result) => {
      invalidateQuote(queryClient, result.quotation_id);
      queryClient.invalidateQueries({ queryKey: queryKeys.approvals.all });
      dispatch(pushToast({
        tone: 'success',
        message: result.auto_approved ? 'No approval needed — quotation approved.' : 'Submitted for approval.',
      }));
    },
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not submit for approval', detail: error.message })),
  });
}

export function useSendQuotation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ id, payload }) => quotationsApi.sendQuotation(id, payload),
    onSuccess: (result) => {
      invalidateQuote(queryClient, result.quotation_id);
      dispatch(pushToast({ tone: 'success', message: 'Quotation sent.' }));
    },
    onError: (error) => {
      const pending = error?.code === 'approval_pending';
      dispatch(pushToast({
        tone: 'danger',
        message: pending ? 'Still awaiting approval' : 'Could not send that quotation',
        detail: pending ? 'It has to be approved before it can be sent.' : error.message,
      }));
    },
  });
}

export function useApprovalQueue(params) {
  return useQuery({
    queryKey: queryKeys.approvals.queue(params),
    queryFn: () => approvalsApi.listApprovals(params),
    placeholderData: (previous) => previous,
  });
}

export function useDecideApproval() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ requestId, payload }) => approvalsApi.decideApproval(requestId, payload),
    onSuccess: (result, { payload }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.quotations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.approvals.all });
      dispatch(pushToast({
        tone: payload.decision === 'approved' ? 'success' : 'warning',
        message: payload.decision === 'approved'
          ? (result.next_step ? 'Approved — escalated to the next step.' : 'Quotation approved.')
          : 'Quotation rejected.',
      }));
    },
    onError: (error) => {
      const already = error?.code === 'already_decided';
      dispatch(pushToast({
        tone: 'danger',
        message: already ? 'Already decided' : 'Could not record that decision',
        detail: already ? 'Somebody else got to this request first.' : error.message,
      }));
    },
  });
}

/** Dropdown options the create/edit form needs. */
export function useQuotationOptions() {
  const accounts = useQuery({
    queryKey: queryKeys.accounts.list({ limit: 200, sort: 'name' }),
    queryFn: () => accountsApi.listAccounts({ limit: 200, sort: 'name' }),
    staleTime: 300_000,
  });
  const opportunities = useQuery({
    queryKey: queryKeys.opportunities.list({ limit: 200, is_closed: 'false' }),
    queryFn: () => opportunitiesApi.listOpportunities({ limit: 200, is_closed: 'false' }),
    staleTime: 300_000,
  });

  const accountRows = accounts.data?.data ?? [];
  const opportunityRows = opportunities.data?.data ?? [];
  const loading = accounts.isLoading || opportunities.isLoading;

  return useMemo(() => ({
    accounts: accountRows.map((a) => ({ value: a.id, label: a.name })),
    opportunities: opportunityRows.map((o) => ({ value: o.id, label: o.name, accountId: o.account?.id })),
    loading,
  }), [accountRows, opportunityRows, loading]);
}
